import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import {
  Loader2, LogOut, CalendarCheck, Users, DollarSign, TrendingUp, LayoutDashboard, Home,
} from "lucide-react";
import { Stat } from "@/components/admin/shared";
import { OverviewTab } from "@/components/admin/OverviewTab";
import { BookingsTab, CustomersTab, DriversTab } from "@/components/admin/tables";
import { DispatchTab, FleetTab } from "@/components/admin/DispatchFleet";
import { CorporateTab, RequestsTab, AutomationsTab } from "@/components/admin/CrmTabs";
import { StaffTab, MyTripsTab } from "@/components/admin/StaffTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = { admin: "Administrator", dispatcher: "Dispatcher", driver: "Driver", readonly: "Read-only staff" };

// Which tabs each role can see
const ROLE_TABS = {
  admin: ["overview", "dispatch", "fleet", "drivers", "bookings", "customers", "corporate", "requests", "automations", "staff"],
  dispatcher: ["dispatch", "fleet", "drivers", "bookings", "requests"],
  readonly: ["overview", "dispatch", "fleet", "drivers", "bookings", "customers", "corporate", "requests", "automations"],
  driver: ["mytrips"],
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "admin";
  const isAdmin = role === "admin";
  const isDriver = role === "driver";
  const canEdit = role === "admin" || role === "dispatcher";
  const tabs = ROLE_TABS[role] || ROLE_TABS.readonly;

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [corporate, setCorporate] = useState([]);
  const [requests, setRequests] = useState([]);
  const [staff, setStaff] = useState([]);
  const [autoLog, setAutoLog] = useState({ logs: [], counts: {} });
  const [runningAuto, setRunningAuto] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);

  const load = useCallback(async () => {
    try {
      if (isDriver) {
        const r = await api.get("/admin/reservations");
        setReservations(r.data);
      } else {
        const reqs = [
          api.get("/admin/bookings"),
          api.get("/admin/vehicles"),
          api.get("/admin/drivers"),
          api.get("/admin/reservations"),
          api.get("/admin/requests"),
        ];
        const [b, v, d, r, rq] = await Promise.all(reqs);
        setBookings(b.data); setVehicles(v.data); setDrivers(d.data);
        setReservations(r.data); setRequests(rq.data);
        if (tabs.includes("overview")) setStats((await api.get("/admin/stats")).data);
        if (tabs.includes("customers")) setCustomers((await api.get("/admin/customers")).data);
        if (tabs.includes("corporate")) setCorporate((await api.get("/admin/corporate")).data);
        if (tabs.includes("automations")) setAutoLog((await api.get("/admin/automations/log")).data);
        if (isAdmin) setStaff((await api.get("/admin/staff")).data);
      }
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isDriver, tabs]);

  useEffect(() => { load(); }, [load]);

  const assign = async (id, field, value) => {
    try {
      const { data } = await api.patch(`/admin/reservations/${id}/assign`, { [field]: value });
      setReservations((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Assigned");
    } catch (e) { toast.error("Assign failed"); }
  };

  const setResStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/reservations/${id}/status`, { status });
      setReservations((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Status updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const toggleVehicle = async (v) => {
    try {
      const { data } = await api.patch(`/admin/vehicles/${v.id}`, { active: !v.active });
      setVehicles((prev) => prev.map((x) => (x.id === v.id ? data : x)));
    } catch (e) { toast.error("Update failed"); }
  };

  const runAutomations = async () => {
    setRunningAuto(true);
    try {
      const { data } = await api.post("/admin/automations/run");
      toast.success(`${data.quote_followups_sent} follow-ups · ${data.balance_reminders_sent} balance reminders · ${data.driver_reminders_sent} driver trip sheets sent`);
      setAutoLog((await api.get("/admin/automations/log")).data);
    } catch (e) { toast.error("Run failed"); } finally { setRunningAuto(false); }
  };

  const resolveRequest = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/requests/${id}`, { status });
      setRequests((prev) => prev.map((x) => (x.id === id ? data : x)));
    } catch (e) { toast.error("Update failed"); }
  };

  const updateCorpStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/corporate/${id}`, { status });
      setCorporate((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success("Status updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const uploadPhoto = async (vehicleId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(vehicleId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/vehicles/${vehicleId}/photo`, fd);
      setVehicles((prev) => prev.map((x) => (x.id === vehicleId ? data : x)));
      toast.success("Photo updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); e.target.value = ""; }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const createStaff = async (payload) => {
    try {
      const { data } = await api.post("/admin/staff", payload);
      setStaff((prev) => [data, ...prev]);
      toast.success("Staff account created");
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
      return false;
    }
  };

  const updateStaff = async (id, updates) => {
    try {
      const { data } = await api.patch(`/admin/staff/${id}`, updates);
      setStaff((prev) => prev.map((s) => (s.id === id ? data : s)));
      toast.success("Updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"); }
  };

  const deleteStaff = async (id) => {
    try {
      await api.delete(`/admin/staff/${id}`);
      setStaff((prev) => prev.filter((s) => s.id !== id));
      toast.success("Staff account removed");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"); }
  };

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  }

  const tabTrigger = "data-[state=active]:bg-white data-[state=active]:text-black";
  const TAB_LABELS = {
    overview: "Overview", dispatch: "Dispatch", fleet: "Fleet", drivers: "Drivers",
    bookings: "Bookings", customers: "Customers", corporate: "Corporate Leads",
    requests: "Requests", automations: "Automations", staff: "Staff", mytrips: "My Trips",
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-white/10 backdrop-blur-2xl bg-black/70 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="h-5 w-5 text-white" />
            <div>
              <span className="font-serif text-xl text-white">{isDriver ? "Driver Portal" : "CRM Dashboard"}</span>
              <p className="text-[10px] tracking-luxe uppercase text-muted-foreground">Autobus Limo · {ROLE_LABEL[role]}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block mr-2">{user?.email}</span>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => navigate("/")}><Home className="h-4 w-4" /></Button>
            <Button data-testid="admin-logout-btn" variant="ghost" size="icon" className="text-white hover:bg-white/10"
              onClick={() => { logout(); navigate("/"); }}><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {stats && tabs.includes("overview") && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat icon={CalendarCheck} label="Total Bookings" value={stats.total_bookings} />
            <Stat icon={TrendingUp} label="Confirmed" value={stats.confirmed_bookings} />
            <Stat icon={Users} label="Customers" value={stats.total_customers} />
            <Stat icon={DollarSign} label="Deposit Revenue" value={stats.deposit_revenue} prefix="$" />
          </div>
        )}

        <Tabs defaultValue={tabs[0]}>
          <TabsList className="bg-secondary border border-white/10 mb-8 flex-wrap h-auto">
            {tabs.map((tk) => (
              <TabsTrigger key={tk} value={tk} data-testid={`tab-${tk}`} className={tabTrigger}>{TAB_LABELS[tk]}</TabsTrigger>
            ))}
          </TabsList>

          {tabs.includes("overview") && stats && <TabsContent value="overview"><OverviewTab stats={stats} /></TabsContent>}
          {tabs.includes("mytrips") && <TabsContent value="mytrips"><MyTripsTab reservations={reservations} backendUrl={BACKEND_URL} /></TabsContent>}
          {tabs.includes("bookings") && <TabsContent value="bookings"><BookingsTab bookings={bookings} updateStatus={updateStatus} readOnly={!canEdit} /></TabsContent>}
          {tabs.includes("customers") && <TabsContent value="customers"><CustomersTab customers={customers} /></TabsContent>}
          {tabs.includes("dispatch") && (
            <TabsContent value="dispatch">
              <DispatchTab reservations={reservations} vehicles={vehicles} drivers={drivers} assign={assign} setResStatus={setResStatus} readOnly={!canEdit} />
            </TabsContent>
          )}
          {tabs.includes("fleet") && (
            <TabsContent value="fleet">
              <FleetTab vehicles={vehicles} toggleVehicle={toggleVehicle} uploadPhoto={uploadPhoto} uploadingId={uploadingId} backendUrl={BACKEND_URL} readOnly={!canEdit} />
            </TabsContent>
          )}
          {tabs.includes("drivers") && <TabsContent value="drivers"><DriversTab drivers={drivers} /></TabsContent>}
          {tabs.includes("corporate") && <TabsContent value="corporate"><CorporateTab corporate={corporate} updateCorpStatus={updateCorpStatus} readOnly={!canEdit} /></TabsContent>}
          {tabs.includes("requests") && <TabsContent value="requests"><RequestsTab requests={requests} resolveRequest={resolveRequest} readOnly={!canEdit} /></TabsContent>}
          {tabs.includes("automations") && (
            <TabsContent value="automations">
              <AutomationsTab autoLog={autoLog} runAutomations={runAutomations} runningAuto={runningAuto} canRun={isAdmin} />
            </TabsContent>
          )}
          {tabs.includes("staff") && (
            <TabsContent value="staff">
              <StaffTab staff={staff} createStaff={createStaff} updateStaff={updateStaff} deleteStaff={deleteStaff} currentUserId={user?.id} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
